/**
 * Bundle 101 (URGENT) - Per-event Survey Campaign Status page.
 *
 * Route: /admin/events/:eventId/survey/campaign (super_admin only)
 *
 * Shows the current campaign status + scheduled send time and lets
 * the operator Pause / Resume / Reschedule.
 *
 * Data sources:
 *   GET  /api/admin/events/{event_id}/survey/campaign/status  (Bundle 98)
 *   POST /api/admin/events/{event_id}/survey/campaign/status  (Bundle 98)
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Pause, Play, CalendarClock, ChevronLeft } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUS_BADGE = {
  active: 'bg-success-50 text-success-700 border-success-200',
  paused: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  draft: 'bg-neutral-100 text-neutral-700 border-neutral-200',
};

const AdminSurveyCampaignPage = () => {
  const { eventId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [reason, setReason] = useState('');

  const fetchCampaign = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/events/${eventId}/survey/campaign/status`,
      );
      setCampaign(data?.campaign || null);
      if (data?.campaign?.scheduled_send_at) {
        // Datetime-local expects YYYY-MM-DDTHH:MM
        try {
          const d = new Date(data.campaign.scheduled_send_at);
          const iso = d.toISOString().slice(0, 16);
          setRescheduleAt(iso);
        } catch (_e) { /* noop */ }
      }
    } catch (e) {
      toast.error('Could not load campaign status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaign(); }, [eventId]);

  const setStatus = async (status, opts = {}) => {
    setBusy(true);
    try {
      const body = { status };
      if (opts.reason) body.reason = opts.reason;
      if (opts.scheduled_send_at) body.scheduled_send_at = opts.scheduled_send_at;
      await api.post(
        `/admin/events/${eventId}/survey/campaign/status`, body,
      );
      toast.success('Campaign updated.');
      await fetchCampaign();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 space-y-6 max-w-3xl"
          data-testid="admin-survey-campaign-page">
      <div className="text-xs text-neutral-500">
        <Link to="/admin/surveys" className="hover:text-primary-900">
          <ChevronLeft className="h-3 w-3 inline" /> Survey Ops
        </Link>
        <span className="mx-1">/</span>
        <Link to={`/admin/events/${eventId}/survey/respondents`}
              className="hover:text-primary-900">
          Respondents
        </Link>
      </div>
      <div>
        <h1 className="font-display text-3xl font-bold text-primary-900">
          Campaign status
        </h1>
        <p className="text-neutral-600 text-sm">
          Pause, resume, or reschedule the survey fan-out.
        </p>
      </div>

      {loading ? (
        <div className="text-neutral-500">Loading...</div>
      ) : !campaign ? (
        <Card>
          <CardContent className="p-6 text-neutral-500"
                        data-testid="campaign-none">
            No campaign has been created for this event yet.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-widest text-neutral-500">Current status</span>
                <Badge className={`${STATUS_BADGE[campaign.status] || STATUS_BADGE.draft} border`}
                        data-testid="campaign-status-badge">
                  {campaign.status}
                </Badge>
              </div>
              <div className="text-sm text-neutral-700">
                Scheduled send at: {' '}
                <span className="font-mono text-primary-900"
                        data-testid="campaign-scheduled-at">
                  {campaign.scheduled_send_at
                    ? new Date(campaign.scheduled_send_at).toLocaleString()
                    : '(not scheduled)'}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                Campaign id: <span className="font-mono">{campaign.id}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="text-sm font-semibold text-primary-900">Actions</div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setStatus('paused', { reason })}
                  disabled={busy || campaign.status === 'paused'}
                  variant="outline"
                  className="border-yellow-300 text-yellow-800 hover:bg-yellow-50"
                  data-testid="campaign-pause-btn"
                >
                  <Pause className="h-4 w-4 mr-2" /> Pause
                </Button>
                <Button
                  onClick={() => setStatus('active', { reason })}
                  disabled={busy || campaign.status === 'active'}
                  className="bg-success-600 text-white hover:bg-success-700"
                  data-testid="campaign-resume-btn"
                >
                  <Play className="h-4 w-4 mr-2" /> Resume
                </Button>
              </div>

              <div className="pt-3 space-y-2 border-t border-neutral-200">
                <Label htmlFor="reschedule-input" className="text-xs">
                  Reschedule send time
                </Label>
                <div className="flex flex-wrap gap-2 items-end">
                  <Input
                    id="reschedule-input"
                    type="datetime-local"
                    value={rescheduleAt}
                    onChange={(e) => setRescheduleAt(e.target.value)}
                    className="w-64"
                    data-testid="campaign-reschedule-input"
                  />
                  <Button
                    onClick={() => setStatus('active', {
                      reason,
                      scheduled_send_at: rescheduleAt
                          ? new Date(rescheduleAt).toISOString() : undefined,
                    })}
                    disabled={busy || !rescheduleAt}
                    className="bg-primary-900 text-white hover:bg-primary-800"
                    data-testid="campaign-reschedule-btn"
                  >
                    <CalendarClock className="h-4 w-4 mr-2" /> Reschedule
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="reason-input" className="text-xs">
                  Reason (for audit log, optional)
                </Label>
                <Input
                  id="reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Weather concerns pushed the follow-up email to next week"
                  data-testid="campaign-reason-input"
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminSurveyCampaignPage;
